import { existsSync, readFileSync } from "node:fs";
import { homedir } from "node:os";
import { basename, join } from "node:path";

/** Individual framework detected in the project */
export interface DetectedFramework {
	name: string;
	version?: string;
}

/** Known stack profiles that can be suggested for installation */
export type KnownStack = "TALL" | "RILT" | "VILT" | "SILT" | "ELYRA_FRAMEWORK";

/** Category of a project verification command */
export type VerificationKind = "typecheck" | "lint" | "test" | "build";

/** A project-defined check the agent should run after making changes */
export interface VerificationCommand {
	kind: VerificationKind;
	/** Shell command to run from the project root */
	command: string;
	/** Where the command was derived from, e.g. "package.json scripts.check", "composer.json", "Cargo.toml" */
	source: string;
}

/** Result of stack detection */
export interface StackDetectionResult {
	/** All detected frameworks */
	frameworks: DetectedFramework[];
	/** Recognized stack profile (if frameworks match a known combination) */
	stack?: KnownStack;
	/** Human-readable summary for system prompt injection */
	summary: string;
	/** Project check commands (typecheck, lint, test, build) derived from manifests */
	verification: VerificationCommand[];
}

/** Detect the technology stack in a project directory */
export function detectStack(cwd: string): StackDetectionResult {
	const frameworks: DetectedFramework[] = [];

	// Parse composer.json for PHP/Laravel dependencies
	const composerPath = join(cwd, "composer.json");
	if (existsSync(composerPath)) {
		try {
			const composer = JSON.parse(readFileSync(composerPath, "utf-8"));
			const deps = { ...composer.require, ...composer["require-dev"] };

			if (deps["laravel/framework"]) {
				frameworks.push({ name: "Laravel", version: extractVersion(deps["laravel/framework"]) });
			}
			if (deps["livewire/livewire"]) {
				frameworks.push({ name: "Livewire", version: extractVersion(deps["livewire/livewire"]) });
			}
			if (deps["livewire/flux"]) {
				frameworks.push({ name: "Flux UI", version: extractVersion(deps["livewire/flux"]) });
			}
			if (deps["inertiajs/inertia-laravel"]) {
				frameworks.push({ name: "Inertia", version: extractVersion(deps["inertiajs/inertia-laravel"]) });
			}
			if (deps["laravel/reverb"]) {
				frameworks.push({ name: "Reverb", version: extractVersion(deps["laravel/reverb"]) });
			}
			if (deps["laravel/sanctum"]) {
				frameworks.push({ name: "Sanctum", version: extractVersion(deps["laravel/sanctum"]) });
			}
			if (deps["filament/filament"]) {
				frameworks.push({ name: "Filament", version: extractVersion(deps["filament/filament"]) });
			}
			if (deps["laravel/ai"]) {
				frameworks.push({ name: "Laravel AI SDK", version: extractVersion(deps["laravel/ai"]) });
			}
		} catch {
			// Ignore malformed composer.json
		}
	}

	// Parse package.json for JS/frontend dependencies
	const packagePath = join(cwd, "package.json");
	if (existsSync(packagePath)) {
		try {
			const pkg = JSON.parse(readFileSync(packagePath, "utf-8"));
			const deps = { ...pkg.dependencies, ...pkg.devDependencies };

			if (deps.vue) {
				frameworks.push({ name: "Vue", version: extractVersion(deps.vue) });
			}
			if (deps.alpinejs) {
				frameworks.push({ name: "Alpine.js", version: extractVersion(deps.alpinejs) });
			}
			if (deps.tailwindcss) {
				frameworks.push({ name: "Tailwind CSS", version: extractVersion(deps.tailwindcss) });
			}
			if (
				deps["@inertiajs/vue3"] ||
				deps["@inertiajs/vue2"] ||
				deps["@inertiajs/react"] ||
				deps["@inertiajs/svelte"]
			) {
				// Inertia frontend adapter -- version from whichever is present
				const inertiaFe =
					deps["@inertiajs/vue3"] ||
					deps["@inertiajs/vue2"] ||
					deps["@inertiajs/react"] ||
					deps["@inertiajs/svelte"];
				if (!frameworks.some((f) => f.name === "Inertia")) {
					frameworks.push({ name: "Inertia", version: extractVersion(inertiaFe) });
				}
			}
			if (deps.react || deps["react-dom"]) {
				frameworks.push({ name: "React", version: extractVersion(deps.react || deps["react-dom"]) });
			}
			if (deps.svelte) {
				frameworks.push({ name: "Svelte", version: extractVersion(deps.svelte) });
			}
			if (deps.typescript) {
				frameworks.push({ name: "TypeScript", version: extractVersion(deps.typescript) });
			}
			if (deps.vite) {
				frameworks.push({ name: "Vite", version: extractVersion(deps.vite) });
			}
		} catch {
			// Ignore malformed package.json
		}
	}

	// Detect Elyra Framework (Rust + Svelte 5 desktop apps). `rata new` scaffolds
	// an `elyra.toml` config file, which is the most reliable signal; fall back
	// to a plain-text check for the `elyra` crate dependency in Cargo.toml (TOML
	// isn't parsed here to avoid adding a parser dependency for one field).
	const cargoPath = join(cwd, "Cargo.toml");
	if (existsSync(join(cwd, "elyra.toml")) || existsSync(cargoPath)) {
		try {
			const cargoText = existsSync(cargoPath) ? readFileSync(cargoPath, "utf-8") : "";
			const hasElyraCrate = /^\s*elyra\s*=/m.test(cargoText) || /\[dependencies\.elyra\]/.test(cargoText);
			if (existsSync(join(cwd, "elyra.toml")) || hasElyraCrate) {
				const versionMatch = cargoText.match(/^\s*elyra\s*=\s*\{[^}]*version\s*=\s*"([^"]+)"/m);
				frameworks.push({ name: "Elyra Framework", version: versionMatch?.[1] });
			}
		} catch {
			// Ignore unreadable Cargo.toml
		}
	}

	const verification = detectVerificationCommands(cwd);

	if (frameworks.length === 0) {
		return { frameworks, summary: "", verification };
	}

	// Detect known stack profiles
	const has = (name: string) => frameworks.some((f) => f.name === name);
	let stack: KnownStack | undefined;

	if (has("Elyra Framework")) {
		stack = "ELYRA_FRAMEWORK";
	} else if (has("Laravel") && has("Tailwind CSS") && has("Alpine.js") && has("Livewire")) {
		stack = "TALL";
	} else if (has("Laravel") && has("Tailwind CSS") && has("Inertia") && has("React")) {
		stack = "RILT";
	} else if (has("Laravel") && has("Tailwind CSS") && has("Inertia") && has("Vue")) {
		stack = "VILT";
	} else if (has("Laravel") && has("Tailwind CSS") && has("Inertia") && has("Svelte")) {
		stack = "SILT";
	}

	// Build human-readable summary
	const frameworkList = frameworks.map((f) => (f.version ? `${f.name} ${f.version}` : f.name)).join(", ");
	let summary = `Detected frameworks: ${frameworkList}`;
	if (stack === "TALL") {
		const hasFlux = has("Flux UI");
		summary = `TALL stack project (Tailwind, Alpine.js, Laravel, Livewire${hasFlux ? ", Flux UI" : ""}).\n${frameworkList}`;
	} else if (stack === "RILT") {
		summary = `RILT stack project (React, Inertia, Laravel, Tailwind).\n${frameworkList}`;
	} else if (stack === "VILT") {
		summary = `VILT stack project (Vue, Inertia, Laravel, Tailwind).\n${frameworkList}`;
	} else if (stack === "SILT") {
		summary = `SILT stack project (Svelte, Inertia, Laravel, Tailwind).\n${frameworkList}`;
	} else if (stack === "ELYRA_FRAMEWORK") {
		summary = `Elyra Framework project (Rust + Svelte 5 desktop app, Laravel-inspired ergonomics).\n${frameworkList}`;
	}

	return { frameworks, stack, summary, verification };
}

// =============================================================================
// Verification command detection
// =============================================================================

const VERIFICATION_KIND_ORDER: VerificationKind[] = ["typecheck", "lint", "test", "build"];

const NODE_TYPECHECK_TOOLS = /\b(tsc|tsgo|svelte-check|vue-tsc)\b/;
const NODE_LINT_TOOLS = /\b(biome|eslint)\b/;
const NPM_DEFAULT_TEST_SCRIPT = /^echo "Error: no test specified" && exit 1$/;

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readJsonObject(path: string): Record<string, unknown> | undefined {
	if (!existsSync(path)) return undefined;
	try {
		const parsed: unknown = JSON.parse(readFileSync(path, "utf-8"));
		return isRecord(parsed) ? parsed : undefined;
	} catch {
		return undefined;
	}
}

function readText(path: string): string | undefined {
	if (!existsSync(path)) return undefined;
	try {
		return readFileSync(path, "utf-8");
	} catch {
		return undefined;
	}
}

/** Narrow an unknown manifest field to a string-valued record (drops non-string entries) */
function stringRecord(value: unknown): Record<string, string> {
	if (!isRecord(value)) return {};
	const out: Record<string, string> = {};
	for (const [key, entry] of Object.entries(value)) {
		if (typeof entry === "string") out[key] = entry;
	}
	return out;
}

function detectNodePackageManager(cwd: string): string {
	if (existsSync(join(cwd, "pnpm-lock.yaml"))) return "pnpm";
	if (existsSync(join(cwd, "yarn.lock"))) return "yarn";
	if (existsSync(join(cwd, "bun.lockb")) || existsSync(join(cwd, "bun.lock"))) return "bun";
	return "npm";
}

function detectNodeVerification(cwd: string): VerificationCommand[] {
	const pkg = readJsonObject(join(cwd, "package.json"));
	if (!pkg) return [];

	const scripts = stringRecord(pkg.scripts);
	const pm = detectNodePackageManager(cwd);
	const result: VerificationCommand[] = [];
	const usedScripts = new Set<string>();

	interface ScriptCandidate {
		name: string;
		accept?: (text: string) => boolean;
	}

	const pickScript = (kind: VerificationKind, candidates: ScriptCandidate[]): boolean => {
		for (const { name, accept } of candidates) {
			const text = scripts[name];
			if (text === undefined || usedScripts.has(name)) continue;
			if (accept && !accept(text)) continue;
			usedScripts.add(name);
			result.push({ kind, command: `${pm} run ${name}`, source: `package.json scripts.${name}` });
			return true;
		}
		return false;
	};

	const hasTypecheckScript = pickScript("typecheck", [
		{ name: "typecheck" },
		{ name: "type-check" },
		{ name: "check", accept: (text) => NODE_TYPECHECK_TOOLS.test(text) },
	]);
	if (!hasTypecheckScript && existsSync(join(cwd, "tsconfig.json"))) {
		const deps = { ...stringRecord(pkg.dependencies), ...stringRecord(pkg.devDependencies) };
		if (deps.typescript) {
			result.push({ kind: "typecheck", command: "npx tsc --noEmit", source: "tsconfig.json" });
		}
	}

	pickScript("lint", [{ name: "lint" }, { name: "check", accept: (text) => NODE_LINT_TOOLS.test(text) }]);
	pickScript("test", [{ name: "test", accept: (text) => !NPM_DEFAULT_TEST_SCRIPT.test(text.trim()) }]);
	pickScript("build", [{ name: "build" }]);

	return result;
}

function detectPhpVerification(cwd: string): VerificationCommand[] {
	const composer = readJsonObject(join(cwd, "composer.json"));
	if (!composer) return [];

	const deps = { ...stringRecord(composer.require), ...stringRecord(composer["require-dev"]) };
	// Composer scripts may be strings or arrays; only the key's presence matters here.
	const scripts = isRecord(composer.scripts) ? composer.scripts : {};
	const result: VerificationCommand[] = [];

	if (deps["phpstan/phpstan"] || deps["larastan/larastan"]) {
		result.push({ kind: "typecheck", command: "vendor/bin/phpstan analyse", source: "composer.json" });
	}

	if (scripts.lint !== undefined) {
		result.push({ kind: "lint", command: "composer lint", source: "composer.json scripts.lint" });
	} else if (deps["laravel/pint"]) {
		result.push({ kind: "lint", command: "vendor/bin/pint --test", source: "composer.json" });
	}

	if (scripts.test !== undefined) {
		result.push({ kind: "test", command: "composer test", source: "composer.json scripts.test" });
	} else if (deps["pestphp/pest"]) {
		result.push({ kind: "test", command: "vendor/bin/pest", source: "composer.json" });
	} else if (deps["phpunit/phpunit"]) {
		result.push({ kind: "test", command: "vendor/bin/phpunit", source: "composer.json" });
	}

	return result;
}

function detectRustVerification(cwd: string): VerificationCommand[] {
	if (!existsSync(join(cwd, "Cargo.toml"))) return [];
	const result: VerificationCommand[] = [
		{ kind: "typecheck", command: "cargo check", source: "Cargo.toml" },
		{ kind: "test", command: "cargo test", source: "Cargo.toml" },
	];
	if (existsSync(join(cwd, "clippy.toml"))) {
		result.push({ kind: "lint", command: "cargo clippy", source: "clippy.toml" });
	}
	return result;
}

function detectPythonVerification(cwd: string): VerificationCommand[] {
	const text = readText(join(cwd, "pyproject.toml"));
	if (text === undefined) return [];
	const result: VerificationCommand[] = [];
	if (/\bmypy\b/.test(text)) {
		result.push({ kind: "typecheck", command: "mypy .", source: "pyproject.toml" });
	}
	if (/\bruff\b/.test(text)) {
		result.push({ kind: "lint", command: "ruff check .", source: "pyproject.toml" });
	}
	if (/\bpytest\b/.test(text)) {
		result.push({ kind: "test", command: "pytest", source: "pyproject.toml" });
	}
	return result;
}

function detectGoVerification(cwd: string): VerificationCommand[] {
	if (!existsSync(join(cwd, "go.mod"))) return [];
	return [
		{ kind: "typecheck", command: "go vet ./...", source: "go.mod" },
		{ kind: "test", command: "go test ./...", source: "go.mod" },
	];
}

/**
 * Derive the project's own check commands from its manifests. Only reports
 * commands whose runner is plausibly present (declared script, dependency, or
 * toolchain manifest); never invents commands. Results are ordered
 * typecheck, lint, test, build and deduplicated by command string.
 */
export function detectVerificationCommands(cwd: string): VerificationCommand[] {
	const detected: VerificationCommand[] = [
		...detectNodeVerification(cwd),
		...detectPhpVerification(cwd),
		...detectRustVerification(cwd),
		...detectPythonVerification(cwd),
		...detectGoVerification(cwd),
	];

	const seen = new Set<string>();
	const result: VerificationCommand[] = [];
	for (const kind of VERIFICATION_KIND_ORDER) {
		for (const cmd of detected) {
			if (cmd.kind !== kind || seen.has(cmd.command)) continue;
			seen.add(cmd.command);
			result.push(cmd);
		}
	}
	return result;
}

/** Render verification commands as a system prompt block, or "" when there are none */
export function formatVerificationForPrompt(commands: VerificationCommand[]): string {
	if (commands.length === 0) return "";
	const lines = commands.map((cmd) => `- ${cmd.kind}: ${cmd.command}  (${cmd.source})`);
	return ["Project checks (run the relevant ones after changes, before reporting done):", ...lines].join("\n");
}

/** Strip semver constraint prefixes (^, ~, >=, etc.) to get a clean version */
function extractVersion(constraint: string | undefined): string | undefined {
	if (!constraint) return undefined;
	const match = constraint.match(/(\d+\.\d+(?:\.\d+)?)/);
	return match?.[1];
}

/** Get the suggested package name for a detected stack */
export function getSuggestedPackage(stack: KnownStack): string {
	switch (stack) {
		case "TALL":
			return "@elyracode/stack-tall";
		case "RILT":
			return "@elyracode/stack-rilt";
		case "VILT":
			return "@elyracode/stack-vilt";
		case "SILT":
			return "@elyracode/stack-silt";
		case "ELYRA_FRAMEWORK":
			return "@elyracode/stack-elyra-framework";
	}
}

// =============================================================================
// Grove environment detection
// =============================================================================

/** Result of Grove site detection */
export interface GroveSiteDetection {
	/** Site name (registered name, or the directory name for parked sites) */
	siteName: string;
	/** The URL Grove serves this site at */
	url: string;
}

function findGroveHome(override?: string): string | undefined {
	if (override) return existsSync(override) ? override : undefined;
	const envHome = process.env.GROVE_HOME;
	if (envHome && existsSync(envHome)) return envHome;
	const candidates = [join(homedir(), "Library", "Application Support", "Grove"), join(homedir(), ".config", "grove")];
	return candidates.find((p) => existsSync(p));
}

function expandTilde(p: string): string {
	return p.startsWith("~/") ? join(homedir(), p.slice(2)) : p;
}

/**
 * Detect whether `cwd` is served by Grove (the local dev environment
 * manager): either registered as a site in Grove's config.toml, or a direct
 * child of a parked path. Uses a line-based scan of the config rather than a
 * TOML parser dependency (same tradeoff as the Cargo.toml check above).
 */
export function detectGroveSite(cwd: string, groveHomeOverride?: string): GroveSiteDetection | undefined {
	const groveHome = findGroveHome(groveHomeOverride);
	if (!groveHome) return undefined;
	const configPath = join(groveHome, "config.toml");
	if (!existsSync(configPath)) return undefined;

	let text: string;
	try {
		text = readFileSync(configPath, "utf-8");
	} catch {
		return undefined;
	}

	let tld = "test";
	const parked: string[] = [];
	interface SiteEntry {
		name?: string;
		path?: string;
		secure?: boolean;
	}
	const sites: SiteEntry[] = [];
	let section: "sites" | "parked" | "other" | "root" = "root";
	let currentSite: SiteEntry | undefined;

	for (const rawLine of text.split("\n")) {
		const line = rawLine.trim();
		if (!line || line.startsWith("#")) continue;

		const header = /^\[\[?([^\]]+?)\]?\]$/.exec(line);
		if (header) {
			const name = header[1].trim();
			if (name === "sites") {
				section = "sites";
				currentSite = {};
				sites.push(currentSite);
			} else if (name === "parked") {
				section = "parked";
			} else {
				section = "other";
			}
			continue;
		}

		const kv = /^([A-Za-z0-9_-]+)\s*=\s*(.+)$/.exec(line);
		if (!kv) continue;
		const key = kv[1];
		const value = kv[2].trim().replace(/^["']/, "").replace(/["']$/, "");

		if (key === "tld" && value) {
			tld = value;
		} else if (section === "parked" && key === "path") {
			parked.push(expandTilde(value));
		} else if (section === "sites" && currentSite) {
			if (key === "name") currentSite.name = value;
			else if (key === "path") currentSite.path = expandTilde(value);
			else if (key === "secure") currentSite.secure = value === "true";
		}
	}

	const registered = sites.find((s) => s.path === cwd && s.name);
	if (registered?.name) {
		const scheme = registered.secure ? "https" : "http";
		return { siteName: registered.name, url: `${scheme}://${registered.name}.${tld}` };
	}

	if (parked.some((p) => cwd.startsWith(`${p}/`) && cwd !== p)) {
		const siteName = basename(cwd);
		return { siteName, url: `http://${siteName}.${tld}` };
	}

	return undefined;
}
